import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { message, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import PrimeModelManager from './PrimeModelManager';
import { PrimeArtifactImportDialog } from './PrimeArtifactImportDialog';
import { PrimeArtifactWorkflowListView } from './PrimeArtifactWorkflowListView';
import { ProcessCentricView } from './ProcessCentricView';
import { ProcessTypeView } from './ProcessTypeView';
import { usePrimeModel } from '../../contexts/PrimeModelContext';
import { useSelector, useDispatch } from 'react-redux';
import { useUser } from '../../contexts/UserContext';
import { PERMISSIONS } from '../../constants/permissions';
import ArtifactCreationDialog from './ArtifactCreationDialog';
import {
  selectHierarchyLoading,
  selectHierarchyError,
} from '../../store/selectors/primeArtifactSelectors';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { fetchPrimeArtifactsHierarchy } from '../../store/actions/primeArtifactActions';
import { PrimeArtifact } from '../../types/primeArtifact';
import { AppDispatch } from '../../store';
import styles from './PrimeModelLayout.module.css';

const PrimeModelLayout: React.FC = React.memo(
  () => {
  const { modelData, selectedArtifact, selectArtifact } = usePrimeModel();
  const loading = useSelector(selectHierarchyLoading);
  const error = useSelector(selectHierarchyError);
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, isLoading: userLoading, hasPermission } = useUser();
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [rootArtifacts, setRootArtifacts] = useState<PrimeArtifact[]>([]);
  const [activeView, setActiveView] = useState<'tree' | 'workflow' | 'processes' | 'process-types'>('tree');
  const [refreshing, setRefreshing] = useState(false);
  const allArtifacts = useSelector((state: any) => state.primeArtifacts.primeArtifacts);

  const loadRootArtifacts = async () => {
    try {
      const roots = await PrimeArtifactService.getRootArtifacts();
      setRootArtifacts(roots);
    } catch (error) {
      console.error('Failed to load root artifacts:', error);
    }
  };

  useEffect(() => {
    void loadRootArtifacts();
    // Load prime artifacts hierarchy
    void dispatch(fetchPrimeArtifactsHierarchy());
  }, [dispatch]);
  
  // Auto-select first artifact if none selected
  useEffect(() => {
    if (!selectedArtifact && allArtifacts && allArtifacts.length > 0 && selectArtifact) {
      const firstArtifact = allArtifacts[0];
      // Auto-selecting first artifact
      selectArtifact(firstArtifact);
    }
  }, [allArtifacts, selectedArtifact, selectArtifact]);

    // Removed verbose selectedArtifact change log

    // Removed verbose modelData change log

    const handleArtifactClick = useCallback(async (artifactId: string) => {
      try {
        // Load the full artifact by ID
        const artifact = await PrimeArtifactService.getById(artifactId);
        if (artifact && selectArtifact) {
          selectArtifact(artifact);
          setActiveView('tree'); // Switch back to tree view to show details
        }
      } catch (error) {
        console.error('Failed to load artifact:', error);
      }
    }, [selectArtifact]);

    const content = useMemo(() => {
      // Removed verbose content recalculation log

      if (activeView === 'workflow') {
        return (
          <div className={styles.primeModelContentContainer}>
            <PrimeArtifactWorkflowListView
              initialSelectedArtifactId={selectedArtifact?.id}
              onArtifactSelect={(artifact) => {
                // Don't navigate away - just update the selected artifact in context
                // This keeps us on the workflow tab
                void PrimeArtifactService.getById(artifact.id)
                  .then((fullArtifact) => {
                    if (selectArtifact) {
                      selectArtifact(fullArtifact);
                    }
                  })
                  .catch((error) => {
                    console.error('Error loading artifact for selection:', error);
                  });
              }}
              onCreateArtifactType={() => {
                // Navigate to artifact type management
                window.location.href = '/artifact-types-manager/';
              }}
              onCreateGlobalProperty={() => {
                // Navigate to global property management
                window.location.href = '/global-properties/';
              }}
            />
          </div>
        );
      }

      if (activeView === 'processes') {
        return (
          <div className={styles.primeModelContentContainer}>
            <ProcessCentricView 
              modelType="prime"
              onProcessClick={(processId) => {
                // Navigate to process details (could be enhanced later)
                message.info(`Process clicked: ${processId}`);
              }}
            />
          </div>
        );
      }

      if (activeView === 'process-types') {
        return (
          <div className={styles.primeModelContentContainer}>
            <ProcessTypeView
              onProcessTypeClick={(type) => {
                message.info(`Process type clicked: ${type}`);
                setActiveView('processes');
              }}
              onProcessClick={(processId) => {
                message.info(`Process clicked: ${processId}`);
              }}
            />
          </div>
        );
      }

      return (
        <div className={styles.primeModelContentContainerTree}>
          {selectedArtifact ? (
            <PrimeModelManager _artifact={selectedArtifact} />
          ) : (
            <div className={styles.noSelection}>
              <p>Select an artifact from the tree to view its details</p>
              {!userLoading && isAuthenticated && hasPermission(PERMISSIONS.WRITE_ARTIFACTS) && (
                <button 
                  className={styles.addFirstArtifactButton}
                  onClick={() => setIsCreateDialogOpen(true)}
                  style={{
                    marginTop: '16px',
                    padding: '8px 16px',
                    backgroundColor: '#1890ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  + Create Your First Prime Artifact
                </button>
              )}
            </div>
          )}
        </div>
      );
    }, [error, loading, modelData?.artifact?.id, selectedArtifact?.id, activeView, handleArtifactClick, userLoading, isAuthenticated, hasPermission]);

    const handleImportComplete = () => {
      // Refresh the tree and reload root artifacts
      void loadRootArtifacts();
      // You might want to trigger a tree refresh here
    };

    const handleRefresh = useCallback(async () => {
      try {
        setRefreshing(true);
        await Promise.all([
          loadRootArtifacts(),
          dispatch(fetchPrimeArtifactsHierarchy()),
        ]);
      } catch (error) {
        console.error('Failed to refresh artifact tree:', error);
        message.error('Failed to refresh artifact tree');
      } finally {
        setRefreshing(false);
      }
    }, [dispatch]);

    return (
      <div className={styles.primeModelLayout}>
        <div className={styles.primeModelHeader}>
          <h1>Prime Model Manager</h1>
          <div className={styles.headerControls}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={refreshing}
              style={{ marginRight: '16px' }}
              title="Refresh artifact tree"
            >
              Refresh
            </Button>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.toggleButton} ${activeView === 'tree' ? styles.active : ''}`}
                onClick={() => setActiveView('tree')}
              >
                Artifact Tree
              </button>
              <button
                className={`${styles.toggleButton} ${activeView === 'workflow' ? styles.active : ''}`}
                onClick={() => setActiveView('workflow')}
              >
                Workflow
              </button>
              <button
                className={`${styles.toggleButton} ${activeView === 'processes' ? styles.active : ''}`}
                onClick={() => setActiveView('processes')}
              >
                Processes
              </button>
              <button
                className={`${styles.toggleButton} ${activeView === 'process-types' ? styles.active : ''}`}
                onClick={() => setActiveView('process-types')}
              >
                Process Types
              </button>
            </div>
            {!userLoading && isAuthenticated && hasPermission(PERMISSIONS.WRITE_ARTIFACTS) && (
              <button className={styles.addTemplateButton} onClick={() => setIsImportDialogOpen(true)}>
                Add Template
              </button>
            )}
          </div>
        </div>
        <div className={styles.primeModelContent}>{content}</div>

        <PrimeArtifactImportDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          onImportComplete={handleImportComplete}
          existingRootArtifacts={rootArtifacts}
        />
        
        <ArtifactCreationDialog
          isOpen={isCreateDialogOpen}
          onClose={async () => {
            setIsCreateDialogOpen(false);
            // Refresh hierarchy after creation
            await dispatch(fetchPrimeArtifactsHierarchy());
          }}
          isChild={false}
        />
      </div>
    );
  },
  () => true
);

PrimeModelLayout.displayName = 'PrimeModelLayout';

export default PrimeModelLayout;
